/**
 * ABTesting Route - 3-Stage A/B Testing Workflow
 *
 * Ported from admin-dashboard with identical GUI.
 * Uses OAuth for email download, LLM for summarization, and multi-model classification.
 *
 * Stage 1: Download emails (Gmail/Outlook via OAuth)
 * Stage 2: Summarize/preprocess (LLM API)
 * Stage 3: Multi-model classification comparison (parallel API calls)
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Email,
  PreprocessedEmail,
  ModelConfig,
  ModelResults,
  ComparisonMetrics,
  StageStatus,
  EmailProvider,
  Stage1Export,
  Stage2Export,
  FALLBACK_MODELS,
  fetchAvailableModels,
} from '../lib/ab-testing/types'
import {
  exportStage3,
} from '../lib/ab-testing/export-import'
import { runWithConcurrencyLimit } from '../lib/ab-testing/concurrencyLimiter'
import {
  runClassification,
  runSummarization,
  ProgressCallback,
} from '../lib/ab-testing/iab-classifier-adapter'
import { computeComparisonMetrics } from '../lib/ab-testing/metrics'
import {
  StageIndicator,
  Stage1Panel,
  Stage2Panel,
  Stage3Panel,
  ResultsDashboard,
} from '../components/ab-testing'
import { getGmailOAuthClient } from '../lib/gmail-oauth-client'
import { getOutlookOAuthClient } from '../lib/outlook-oauth-client'
// IndexedDBStore no longer needed - using memoryStore from StoreContext
import { getPendingOAuthAction, setPendingOAuthAction, hasPendingOAuthAction, clearStaleOAuthState, ReAuthRequiredError } from '../lib/oauth-integration'
import { useStore } from '../contexts/StoreContext'
import { useAuth } from '../contexts/AuthContext'
import { createStoreAdapter } from '../lib/ab-testing/store-adapter'
import { NS } from '@ownyou/shared-types'
import { IAB_INPUT_PREPROCESSING_ROUTE_ID } from '../lib/llm-routing'

function toStage1Emails(emails: PreprocessedEmail[]): Email[] {
  return emails.map(e => ({
    id: e.id,
    subject: e.subject,
    from: e.from,
    body: e.body,
    date: e.date,
  }))
}

export function ABTesting() {
  // Get MemoryStore from StoreContext and create BaseStore adapter for full classifier
  const { store: memoryStore, isReady: memoryStoreReady } = useStore()
  const { wallet } = useAuth()
  const userId = wallet?.address ?? 'anonymous'

  // Use memoryStore for persistence (same store as Results page reads from)

  // Stage status
  const [stageStatus, setStageStatus] = useState<{
    download: StageStatus
    preprocess: StageStatus
    classify: StageStatus
  }>({
    download: 'idle',
    preprocess: 'idle',
    classify: 'idle',
  })

  // Dynamic models from API (loaded on mount)
  const [availableModels, setAvailableModels] = useState<ModelConfig[]>(FALLBACK_MODELS)
  const [modelsLoading, setModelsLoading] = useState(true)

  // Stage 1 state
  const [downloadedEmails, setDownloadedEmails] = useState<Email[]>([])
  const [downloadConfig, setDownloadConfig] = useState<{
    provider: EmailProvider
    maxEmails: number
  }>({
    provider: 'outlook',
    maxEmails: 50,
  })

  // Stage 2 state
  const [preprocessedEmails, setPreprocessedEmails] = useState<PreprocessedEmail[]>([])
  const [preprocessConfig, setPreprocessConfig] = useState<Stage2Export['preprocessConfig']>({
    routeId: IAB_INPUT_PREPROCESSING_ROUTE_ID,
    summarizerProvider: 'openai',
    summarizerModel: 'gpt-4o-mini',
  })

  // Stage 3 state - 4 independent model selections for maximum flexibility
  const [selectedModels, setSelectedModels] = useState<ModelConfig[]>([
    FALLBACK_MODELS[0], // GPT-4o-mini by default
  ])
  const [classificationResults, setClassificationResults] = useState<Map<string, ModelResults>>(new Map())
  const [modelProgress, setModelProgress] = useState<Map<string, 'started' | 'completed' | 'error'>>(new Map())

  // Comparison metrics
  const [comparisonMetrics, setComparisonMetrics] = useState<ComparisonMetrics | null>(null)

  // memoryStore from StoreContext is used for persistence (initialized by StoreProvider)

  // Clear stale OAuth state on mount (auto-recovery from failed OAuth flows)
  useEffect(() => {
    const checkAndClearStaleState = async () => {
      const result = await clearStaleOAuthState()
      if (result.wasStale) {
        console.log('[A/B Testing] Cleared stale OAuth state:', result.reason)
      }
    }
    checkAndClearStaleState()
  }, [])

  // Load persisted A/B testing session from memoryStore on page load
  useEffect(() => {
    if (!memoryStore || !memoryStoreReady) return

    const loadPersistedData = async () => {
      try {
        console.log('[A/B Testing Persistence] Loading session from IndexedDB...')

        // Check for pending OAuth action BEFORE loading config
        // Also check if we've already processed a pending action (race condition fix)
        const hasPending = hasPendingOAuthAction() || pendingActionProcessed.current
        if (hasPending) {
          console.log('[A/B Testing Persistence] Pending OAuth action detected or already processed, skipping config restoration')
        }

        // Load stage 1 data (downloaded emails)
        let loadedStage1FromStore = false
        const stage1Data = await memoryStore.get<{ emails: Email[], config: any, stageStatus: StageStatus }>([...NS.abTestingStage1(userId)], 'latest')
        if (stage1Data) {
          const data = stage1Data
          setDownloadedEmails(data.emails || [])
          loadedStage1FromStore = true
          if (data.config && !hasPending) {
            setDownloadConfig(data.config)
          }
          if (data.stageStatus === 'completed') {
            setStageStatus(s => ({ ...s, download: 'completed' }))
          }
          console.log(`[A/B Testing Persistence] Loaded ${data.emails?.length || 0} downloaded emails`)
        }

        // Load stage 2 data (preprocessed emails)
        const stage2Data = await memoryStore.get<{ emails: PreprocessedEmail[], config: any, stageStatus: StageStatus }>([...NS.abTestingStage2(userId)], 'latest')
        if (stage2Data) {
          const data = stage2Data
          const stage2Emails = data.emails || []
          setPreprocessedEmails(stage2Emails)
          if (!loadedStage1FromStore && stage2Emails.length > 0) {
            setDownloadedEmails(toStage1Emails(stage2Emails))
            setStageStatus(s => ({ ...s, download: 'completed' }))
            console.log(`[A/B Testing Persistence] Reconstructed ${stage2Emails.length} downloaded emails from Stage 2 persistence`)
          }
          if (data.config) {
            setPreprocessConfig({
              ...data.config,
              routeId: IAB_INPUT_PREPROCESSING_ROUTE_ID,
            })
          }
          if (data.stageStatus === 'completed') {
            setStageStatus(s => ({ ...s, preprocess: 'completed' }))
          }
          console.log(`[A/B Testing Persistence] Loaded ${data.emails?.length || 0} preprocessed emails`)
        }

        // Load stage 3 data (classification results only - NOT model selections)
        const stage3Data = await memoryStore.get<{
          results: Record<string, ModelResults>,
          metrics: ComparisonMetrics | null,
          stageStatus: StageStatus
        }>([...NS.abTestingResults(userId)], 'latest')
        if (stage3Data) {
          const data = stage3Data
          if (data.results) {
            const resultsMap = new Map<string, ModelResults>()
            Object.entries(data.results).forEach(([key, value]) => {
              resultsMap.set(key, value)
            })
            setClassificationResults(resultsMap)
          }
          if (data.metrics) {
            setComparisonMetrics(data.metrics)
          }
          if (data.stageStatus === 'completed') {
            setStageStatus(s => ({ ...s, classify: 'completed' }))
          }
          console.log(`[A/B Testing Persistence] Loaded classification results`)
        }

        console.log('[A/B Testing Persistence] Session loaded successfully')
      } catch (err) {
        console.error('[A/B Testing Persistence] Failed to load session:', err)
      }
    }

    loadPersistedData()
  }, [memoryStore, memoryStoreReady, userId])

  // Load models from API on mount - always fresh, never persisted
  useEffect(() => {
    const loadModels = async () => {
      console.log('[A/B Testing] Loading fresh models from API...')
      setModelsLoading(true)
      try {
        const models = await fetchAvailableModels(true) // Force refresh
        setAvailableModels(models)

        // Set default selections from fresh API response
        if (models.length > 0) {
          // Stage 3: Set first model as default
          setSelectedModels([models[0]])
          console.log(`[A/B Testing] Stage 3 default: ${models[0].provider}:${models[0].model}`)

          // Stage 2: Update preprocessConfig to use first model for current provider
          // Find first OpenAI model (or first model if no OpenAI)
          const openaiModels = models.filter(m => m.provider === 'openai')
          const firstOpenAI = openaiModels[0] || models[0]
          setPreprocessConfig(prev => ({
            ...prev,
            routeId: IAB_INPUT_PREPROCESSING_ROUTE_ID,
            summarizerProvider: firstOpenAI.provider,
            summarizerModel: firstOpenAI.model,
          }))
          console.log(`[A/B Testing] Stage 2 default: ${firstOpenAI.provider}:${firstOpenAI.model}`)
        } else {
          setSelectedModels([])
        }
        console.log(`[A/B Testing] Loaded ${models.length} models from API`)
      } catch (error) {
        console.error('[A/B Testing] Failed to load models:', error)
        // Keep using fallback models
      } finally {
        setModelsLoading(false)
      }
    }
    loadModels()
  }, [wallet?.address])

  // Track if we've already checked for pending OAuth actions
  const pendingActionChecked = useRef(false)
  // Track if we've processed a pending action (to prevent session loading from overwriting)
  const pendingActionProcessed = useRef(false)

  // Check for pending OAuth action after page load (auto-continue OAuth flow)
  useEffect(() => {
    if (pendingActionChecked.current) return
    pendingActionChecked.current = true

    const checkPendingAction = async () => {
      const pending = getPendingOAuthAction()
      if (pending?.action === 'download_both') {
        console.log('[A/B Testing] Found pending action: download_both, continuing OAuth flow...')

        // Mark that we've processed a pending action (prevents session loading from overwriting)
        pendingActionProcessed.current = true

        // Restore the download config from the pending action
        if (pending.provider || pending.maxEmails) {
          console.log(`[A/B Testing] Restoring config: provider=${pending.provider}, maxEmails=${pending.maxEmails}`)
          setDownloadConfig(prev => ({
            provider: (pending.provider as EmailProvider) || prev.provider,
            maxEmails: pending.maxEmails || prev.maxEmails,
          }))
        }

        // Small delay to ensure page is fully rendered and state is updated
        setTimeout(() => {
          // Trigger download which will check auth status and continue appropriately
          const downloadBtn = document.querySelector('[data-action="download"]') as HTMLButtonElement
          if (downloadBtn) {
            downloadBtn.click()
          }
        }, 500)
      }
    }

    checkPendingAction()
  }, [])

  // Compute current stage
  const currentStage: 1 | 2 | 3 =
    stageStatus.classify === 'completed' ? 3 :
    stageStatus.preprocess === 'completed' ? 3 :
    stageStatus.download === 'completed' ? 2 : 1

  // Stage 1: Download emails using direct OAuth clients
  const handleDownload = async () => {
    setStageStatus(s => ({ ...s, download: 'running' }))
    try {
      const emails: Email[] = []
      const { provider, maxEmails } = downloadConfig

      // For "both" mode: download maxEmails from EACH provider
      const emailsPerProvider = maxEmails

      // For "both" mode, check authorization status FIRST before any downloads
      if (provider === 'both') {
        const gmailOAuthClient = getGmailOAuthClient()
        const outlookOAuthClient = getOutlookOAuthClient()

        const gmailAuthorized = await gmailOAuthClient.isAuthorized()
        const outlookAuthorized = await outlookOAuthClient.isAuthorized()

        console.log(`[A/B Testing] Authorization status - Gmail: ${gmailAuthorized}, Outlook: ${outlookAuthorized}`)

        // If neither is authorized, start with Gmail OAuth
        if (!gmailAuthorized && !outlookAuthorized) {
          console.log('[A/B Testing] Neither provider authorized, starting Gmail OAuth...')
          setPendingOAuthAction('download_both', { provider, maxEmails })
          await gmailOAuthClient.authorize()
          return
        }

        // If only Outlook not authorized, start Outlook OAuth
        if (gmailAuthorized && !outlookAuthorized) {
          console.log('[A/B Testing] Gmail authorized but Outlook not, starting Outlook OAuth...')
          setPendingOAuthAction('download_both', { provider, maxEmails })
          await outlookOAuthClient.authorize()
          return
        }

        // If only Gmail not authorized, start Gmail OAuth
        if (!gmailAuthorized && outlookAuthorized) {
          console.log('[A/B Testing] Outlook authorized but Gmail not, starting Gmail OAuth...')
          setPendingOAuthAction('download_both', { provider, maxEmails })
          await gmailOAuthClient.authorize()
          return
        }

        console.log('[A/B Testing] Both providers authorized, proceeding with downloads...')
      }

      // Download from Gmail
      if (provider === 'gmail' || provider === 'both') {
        try {
          const gmailOAuthClient = getGmailOAuthClient()
          const isAuthorized = await gmailOAuthClient.isAuthorized()

          if (!isAuthorized) {
            console.log('[A/B Testing] Gmail not authorized, starting OAuth flow...')
            await gmailOAuthClient.authorize()
            return
          }

          const gmailClient = await gmailOAuthClient.getClient()
          const gmailResponse = await gmailClient.listMessages(undefined, emailsPerProvider)

          console.log('[A/B Testing] Gmail API response:', gmailResponse)
          console.log('[A/B Testing] Gmail messages count:', gmailResponse.messages?.length || 0)

          if (gmailResponse.messages && gmailResponse.messages.length > 0) {
            // Use concurrency limiter to avoid Gmail API rate limits (429 errors)
            // Gmail API allows ~25 requests/second, but 5 concurrent is safer
            const messageTasks = gmailResponse.messages.map((msg: { id: string }) =>
              () => gmailClient.getMessage(msg.id, 'full')
            )
            const messages = await runWithConcurrencyLimit(messageTasks, 5)

            // Filter out any errors and log them
            const validMessages = messages.filter((msg): msg is any => !(msg instanceof Error))
            const errorCount = messages.length - validMessages.length
            if (errorCount > 0) {
              console.warn(`[A/B Testing] ${errorCount} Gmail messages failed to fetch`)
            }

            const gmailEmails = validMessages.map((msg: any) => ({
              id: `gmail_${msg.id}`,
              from: gmailClient.getHeader(msg, 'From') || 'Unknown',
              subject: gmailClient.getHeader(msg, 'Subject') || 'No Subject',
              body: gmailClient.getPlainTextBody(msg) || gmailClient.getHtmlBody(msg) || 'No body',
              date: gmailClient.getHeader(msg, 'Date') || new Date().toISOString(),
            }))

            emails.push(...gmailEmails)
            console.log(`[A/B Testing] Downloaded ${gmailEmails.length} Gmail emails`)
          }
        } catch (err) {
          // Check if re-authentication is required (refresh token expired)
          if (err instanceof ReAuthRequiredError) {
            console.log('[A/B Testing] Gmail re-authentication required, starting OAuth flow...')
            const gmailOAuthClient = getGmailOAuthClient()
            // Store pending action so we can resume after OAuth completes
            setPendingOAuthAction(provider === 'both' ? 'download_both' : 'download_gmail', { provider, maxEmails })
            await gmailOAuthClient.authorize()
            return // OAuth will redirect, handleDownload will be called again after callback
          }
          console.error('[A/B Testing] Failed to download Gmail emails:', err)
          if (provider === 'gmail') throw err
        }
      }

      // Download from Outlook
      if (provider === 'outlook' || provider === 'both') {
        try {
          const outlookOAuthClient = getOutlookOAuthClient()
          const isAuthorized = await outlookOAuthClient.isAuthorized()

          if (!isAuthorized) {
            console.log('[A/B Testing] Outlook not authorized, starting OAuth flow...')
            await outlookOAuthClient.authorize()
            return
          }

          const outlookClient = await outlookOAuthClient.getClient()
          const outlookResponse = await outlookClient.listMessages(undefined, emailsPerProvider)

          console.log('[A/B Testing] Outlook API response:', outlookResponse)
          console.log('[A/B Testing] Outlook messages count:', outlookResponse.value?.length || 0)

          if (outlookResponse.value && outlookResponse.value.length > 0) {
            const outlookEmails = outlookResponse.value.map((msg: any) => ({
              id: `outlook_${msg.id}`,
              from: msg.from?.emailAddress?.address || 'Unknown',
              subject: msg.subject || 'No Subject',
              body: msg.body?.content || 'No body',
              date: msg.receivedDateTime || new Date().toISOString(),
            }))

            emails.push(...outlookEmails)
            console.log(`[A/B Testing] Downloaded ${outlookEmails.length} Outlook emails`)
          }
        } catch (err) {
          // Check if re-authentication is required (refresh token expired)
          if (err instanceof ReAuthRequiredError) {
            console.log('[A/B Testing] Outlook re-authentication required, starting OAuth flow...')
            const outlookOAuthClient = getOutlookOAuthClient()
            // Store pending action so we can resume after OAuth completes
            setPendingOAuthAction(provider === 'both' ? 'download_both' : 'download_outlook', { provider, maxEmails })
            await outlookOAuthClient.authorize()
            return // OAuth will redirect, handleDownload will be called again after callback
          }
          console.error('[A/B Testing] Failed to download Outlook emails:', err)
          if (provider === 'outlook') throw err
        }
      }

      if (emails.length === 0) {
        throw new Error('No emails found. Please check your OAuth connection.')
      }

      console.log(`[A/B Testing] Total emails downloaded: ${emails.length}`)

      setDownloadedEmails(emails)
      setStageStatus(s => ({ ...s, download: 'completed' }))
      // Reset later stages
      setPreprocessedEmails([])
      setClassificationResults(new Map())
      setComparisonMetrics(null)
      setStageStatus(s => ({ ...s, preprocess: 'idle', classify: 'idle' }))

      // Persist to IndexedDB
      if (memoryStore) {
        await memoryStore.put([...NS.abTestingStage1(userId)], 'latest', {
          emails,
          config: downloadConfig,
          stageStatus: 'completed',
          savedAt: new Date().toISOString(),
        })
        await memoryStore.delete([...NS.abTestingStage2(userId)], 'latest')
        await memoryStore.delete([...NS.abTestingResults(userId)], 'latest')
        console.log('[A/B Testing Persistence] Stage 1 saved to IndexedDB')
      }
    } catch (error) {
      console.error('Download error:', error)
      setStageStatus(s => ({ ...s, download: 'error' }))
      throw error
    }
  }

  // Stage 1: Import emails
  const handleStage1Import = async (data: Stage1Export) => {
    const importedConfig = {
      provider: data.downloadConfig.provider,
      maxEmails: data.downloadConfig.maxEmails,
    }

    setDownloadedEmails(data.emails)
    setDownloadConfig(importedConfig)
    // Reset later stages
    setPreprocessedEmails([])
    setClassificationResults(new Map())
    setComparisonMetrics(null)
    setStageStatus(s => ({ ...s, download: 'completed', preprocess: 'idle', classify: 'idle' }))

    if (memoryStore) {
      await memoryStore.put([...NS.abTestingStage1(userId)], 'latest', {
        emails: data.emails,
        config: importedConfig,
        stageStatus: 'completed',
        savedAt: new Date().toISOString(),
      })
      await memoryStore.delete([...NS.abTestingStage2(userId)], 'latest')
      await memoryStore.delete([...NS.abTestingResults(userId)], 'latest')
      console.log('[A/B Testing Persistence] Stage 1 import saved to IndexedDB')
    }
  }

  // Stage 2: Pre-process emails
  const handlePreprocess = async () => {
    setStageStatus(s => ({ ...s, preprocess: 'running' }))
    try {
      const processed = await runSummarization(
        downloadedEmails,
        preprocessConfig.summarizerProvider,
        preprocessConfig.summarizerModel,
        'ab_testing_user'
      )
      setPreprocessedEmails(processed)
      setStageStatus(s => ({ ...s, preprocess: 'completed' }))
      // Reset later stage
      setClassificationResults(new Map())
      setComparisonMetrics(null)
      setStageStatus(s => ({ ...s, classify: 'idle' }))

      // Persist to IndexedDB
      if (memoryStore) {
        await memoryStore.put([...NS.abTestingStage2(userId)], 'latest', {
          emails: processed,
          config: {
            ...preprocessConfig,
            routeId: IAB_INPUT_PREPROCESSING_ROUTE_ID,
          },
          stageStatus: 'completed',
          savedAt: new Date().toISOString(),
        })
        await memoryStore.delete([...NS.abTestingResults(userId)], 'latest')
        console.log('[A/B Testing Persistence] Stage 2 saved to IndexedDB')
      }
    } catch (error) {
      console.error('Preprocess error:', error)
      setStageStatus(s => ({ ...s, preprocess: 'error' }))
      throw error
    }
  }

  // Stage 2: Import preprocessed emails
  const handleStage2Import = async (data: Stage2Export) => {
    const stage2Config = {
      routeId: IAB_INPUT_PREPROCESSING_ROUTE_ID,
      summarizerProvider: data.preprocessConfig.summarizerProvider,
      summarizerModel: data.preprocessConfig.summarizerModel,
    }
    const stage1Emails = toStage1Emails(data.emails)

    setPreprocessedEmails(data.emails)
    setPreprocessConfig(stage2Config)
    // Also set stage 1 as complete since we have the emails
    setDownloadedEmails(stage1Emails)
    // Reset later stage
    setClassificationResults(new Map())
    setComparisonMetrics(null)
    setStageStatus(s => ({ ...s, download: 'completed', preprocess: 'completed', classify: 'idle' }))

    if (memoryStore) {
      await memoryStore.put([...NS.abTestingStage1(userId)], 'latest', {
        emails: stage1Emails,
        config: {
          ...downloadConfig,
          maxEmails: stage1Emails.length || downloadConfig.maxEmails,
        },
        stageStatus: 'completed',
        savedAt: new Date().toISOString(),
      })
      await memoryStore.put([...NS.abTestingStage2(userId)], 'latest', {
        emails: data.emails,
        config: stage2Config,
        stageStatus: 'completed',
        savedAt: new Date().toISOString(),
      })
      await memoryStore.delete([...NS.abTestingResults(userId)], 'latest')
      console.log('[A/B Testing Persistence] Stage 2 import saved to IndexedDB')
    }
  }

  // Stage 3: Model toggle
  const handleModelToggle = (model: ModelConfig) => {
    setSelectedModels(prev => {
      const exists = prev.some(m => m.provider === model.provider && m.model === model.model)
      if (exists) {
        return prev.filter(m => !(m.provider === model.provider && m.model === model.model))
      } else {
        return [...prev, model]
      }
    })
  }

  // Stage 3: Progress callback
  const handleProgress: ProgressCallback = useCallback((modelKey, status) => {
    setModelProgress(prev => new Map(prev).set(modelKey, status))
  }, [])

  // Stage 3: Run classification
  // Sprint 11d: Uses full IAB classifier workflow (REQUIRED - no fallback)
  const handleClassify = async () => {
    setStageStatus(s => ({ ...s, classify: 'running' }))
    setModelProgress(new Map())

    // REQUIRE store - no fallback to simple classification
    // Store should ALWAYS be available since A/B Testing requires OAuth → requires wallet → triggers StoreContext
    if (!memoryStore || !memoryStoreReady) {
      console.error('[A/B Testing] Store not available - this should not happen if authenticated')
      setStageStatus(s => ({ ...s, classify: 'error' }))
      alert('Store not ready. Please wait a moment and try again.')
      return
    }

    try {
      // Create BaseStore adapter from MemoryStore for full workflow
      const baseStore = createStoreAdapter(memoryStore)
      console.log('[A/B Testing] Using full workflow classification')

      const results = await runClassification(
        preprocessedEmails,
        selectedModels,
        'ab_testing_user',
        baseStore,
        handleProgress
      )
      setClassificationResults(results)

      // Compute metrics
      const emailIds = preprocessedEmails.map(e => e.id)
      const metrics = computeComparisonMetrics(results, emailIds)
      setComparisonMetrics(metrics)

      setStageStatus(s => ({ ...s, classify: 'completed' }))

      // Persist to IndexedDB
      if (memoryStore) {
        const resultsRecord: Record<string, ModelResults> = {}
        results.forEach((value, key) => {
          resultsRecord[key] = value
        })
        await memoryStore.put([...NS.abTestingResults(userId)], 'latest', {
          results: resultsRecord,
          metrics,
          models: selectedModels,
          stageStatus: 'completed',
          savedAt: new Date().toISOString(),
        })
        console.log('[A/B Testing Persistence] Stage 3 saved to IndexedDB')
      }
    } catch (error) {
      console.error('Classification error:', error)
      setStageStatus(s => ({ ...s, classify: 'error' }))
      throw error
    }
  }

  // Stage 3: Export results
  const handleStage3Export = () => {
    if (comparisonMetrics) {
      exportStage3(selectedModels, classificationResults, comparisonMetrics)
    }
  }

  // Clear all session data (both state and IndexedDB)
  const handleClearSession = async () => {
    if (!confirm('Are you sure you want to clear all A/B testing data? This cannot be undone.')) {
      return
    }

    // Clear state
    setDownloadedEmails([])
    setPreprocessedEmails([])
    setClassificationResults(new Map())
    setComparisonMetrics(null)
    setModelProgress(new Map())
    setStageStatus({
      download: 'idle',
      preprocess: 'idle',
      classify: 'idle',
    })
    setSelectedModels([availableModels[0] || FALLBACK_MODELS[0]])

    // Clear IndexedDB
    if (memoryStore) {
      try {
        await memoryStore.delete([...NS.abTestingStage1(userId)], 'latest')
        await memoryStore.delete([...NS.abTestingStage2(userId)], 'latest')
        await memoryStore.delete([...NS.abTestingResults(userId)], 'latest')
        console.log('[A/B Testing Persistence] Session cleared from IndexedDB')
      } catch (err) {
        console.error('[A/B Testing Persistence] Failed to clear session:', err)
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">A/B Model Testing</h1>
            <p className="text-gray-600">
              Compare IAB Taxonomy classification across different LLM models
            </p>
          </div>
          {(downloadedEmails.length > 0 || preprocessedEmails.length > 0 || classificationResults.size > 0) && (
            <button
              onClick={handleClearSession}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
            >
              Clear Session
            </button>
          )}
        </div>

        {/* Stage indicator */}
        <StageIndicator currentStage={currentStage} stageStatus={stageStatus} />

        {/* Stage panels */}
        <div className="space-y-6">
          {/* Stage 1 */}
          <Stage1Panel
            status={stageStatus.download}
            emails={downloadedEmails}
            config={downloadConfig}
            onConfigChange={setDownloadConfig}
            onDownload={handleDownload}
            onImport={handleStage1Import}
            onExport={() => {}}
          />

          {/* Stage 2 */}
          <Stage2Panel
            status={stageStatus.preprocess}
            emails={preprocessedEmails}
            config={preprocessConfig}
            sourceEmailCount={downloadedEmails.length}
            onConfigChange={setPreprocessConfig}
            onPreprocess={handlePreprocess}
            onImport={handleStage2Import}
            onExport={() => {}}
            disabled={stageStatus.download !== 'completed'}
            availableModels={availableModels}
            modelsLoading={modelsLoading}
          />

          {/* Stage 3 */}
          <Stage3Panel
            status={stageStatus.classify}
            selectedModels={selectedModels}
            results={classificationResults}
            sourceEmailCount={preprocessedEmails.length}
            onModelToggle={handleModelToggle}
            onClassify={handleClassify}
            onExport={handleStage3Export}
            disabled={stageStatus.preprocess !== 'completed'}
            progress={modelProgress}
            availableModels={availableModels}
            modelsLoading={modelsLoading}
          />

          {/* Results Dashboard */}
          {comparisonMetrics && classificationResults.size > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Results</h2>
              <ResultsDashboard
                metrics={comparisonMetrics}
                results={classificationResults}
                emails={preprocessedEmails}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ABTesting
